import {Component, computed, resource, signal, Signal, ViewChild} from '@angular/core';
import {SidebarComponent} from '@app/navigation/sidebar/sidebar.component';
import {DATA_SEARCH_FILTERS} from '@app/search/filters/data';
import {FILTERS_TOKEN} from '@app/search/filters/filter';
import {SearchBarComponent} from '@app/search/search-bar/search-bar.component';
import {DATA_SEARCH_SORT_OPTIONS, SortOption} from '@app/search/search-bar/search-bar.config';
import {SearchResultsComponent} from '@app/search/search-results/search-results.component';
import {InfiniteScrollDirective} from '@app/shared/directives/infinite-scroll.directive';
import {SearchIndexName, SearchRecordType} from '@models/search';
import {LandingServiceConfigService} from '@services/config/landing-service-config.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {UserService} from '@services/user/user.service';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {TileInfo} from '@shared/types/tile-info';
import {lastValueFrom} from 'rxjs';

@Component({
  selector: 'app-private-data-enclave',
  standalone: true,
  imports: [SearchBarComponent, SidebarComponent, SearchResultsComponent],
  templateUrl: './private-data-enclave.component.html',
  providers: [{provide: FILTERS_TOKEN, useValue: DATA_SEARCH_FILTERS}],
})
export class PrivateDataEnclaveComponent implements SectionHeaderActions {
  @ViewChild(SearchBarComponent) searchBar!: SearchBarComponent;
  @ViewChild(InfiniteScrollDirective) infiniteScrollDirective?: InfiniteScrollDirective;

  sort: SortOption[] = DATA_SEARCH_SORT_OPTIONS;
  tiles = signal<TileInfo[] | undefined>(undefined);
  loading = signal(false);
  recordType: SearchRecordType = 'projects';
  index: SearchIndexName = 'Projects';
  canCreateProjects: Signal<boolean>;

  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const actions: SectionHeaderAction[] = [
      {
        target: '/help/private-data-enclave/search',
        targetTab: '_blank',
        caption: 'Help',
        color: 'var(--mat-sys-primary)',
        icon: 'help_outline',
      },
    ];
    if (this.canCreateProjects()) {
      actions.unshift({
        target: '/private-data-enclave/project/add',
        caption: 'Add Project',
        color: 'var(--mat-sys-primary)',
        icon: 'add_circle_outline',
      });
    }

    return actions;
  });

  constructor(userService: UserService, lscs: LandingServiceConfigService, lsbs: LandingServiceBrokerService) {
    const user = userService.userSignal;
    const org = computed(() => user()?.organization?.name);
    const services = lsbs.getResource(org);
    const config = lscs.getResource(org);

    const isAdmin = resource({
      params: () => {
        if (user() && org() && services.hasValue()) return {};
        return undefined;
      },
      loader: async () => {
        const userData = await lastValueFrom(services.value().UsersService.getUser('me'));
        return userData.is_admin;
      },
    });

    this.canCreateProjects = computed(
      () =>
        !user() ||
        !config.hasValue() ||
        !isAdmin.hasValue() ||
        (isAdmin.value() || !config.value().project_creation_admin_only),
    );
  }

  async onInfiniteScroll(infiniteScroll: InfiniteScrollDirective) {
    await this.searchBar.loadNextPage();
    infiniteScroll.markLoadComplete();
  }
}
